import { createReducer, on } from '@ngrx/store';
import { AdOrderDraftActions } from '../actions/ad-order-draft.action';
import { PlatformTelegram } from '@smmof/entity';
import { AdOrderItemSettingsInterface } from '@smmof/interface';
import { AdPublicationCreateDto } from '@smmof/dto';

export interface AdOrderItemDraft {
  platform: PlatformTelegram;
  settings: AdOrderItemSettingsInterface;
  amount: number;
}

export interface AdOrderDraftState {
  adPublication: AdPublicationCreateDto;
  adOrderItems: AdOrderItemDraft[];
  totalPrice: number;
  serverDraftId?: number | null;
  updatedAt?: string | null;
}

export const initialAdOrderDraftState: AdOrderDraftState = {
  adPublication: null,
  adOrderItems: [],
  totalPrice: 0,
  serverDraftId: null,
  updatedAt: null,
};

export const adOrderDraftReducer = createReducer(
  initialAdOrderDraftState,

  // Добавление платформы в черновик заказа
  on(AdOrderDraftActions.addPlatformToAdOrderDraft, (state, { platform }) => {
    const platformExists = state.adOrderItems.some(item => item.platform.id === platform.id);

    if (platformExists) {
      return state;
    }

    const defaultSettings: AdOrderItemSettingsInterface = {
      days: 1,
      topHours: 0,
      placementDate: new Date(),
    };

    const amount = calculateAmountDraft(platform, defaultSettings);

    const newOrderItem: AdOrderItemDraft = {
      platform,
      settings: defaultSettings,
      amount,
    };

    const newTotalPrice = state.totalPrice + amount;

    return {
      ...state,
      adOrderItems: [...state.adOrderItems, newOrderItem],
      totalPrice: newTotalPrice,
    };
  }),

  // Обновление настроек размещения для элемента заказа
  on(AdOrderDraftActions.updateOrderItemSettings, (state, { platformId, settings }) => {
    const itemIndex = state.adOrderItems.findIndex(item => item.platform.id === platformId);

    if (itemIndex === -1) {
      return state;
    }

    const orderItem = state.adOrderItems[itemIndex];

    const newAmount = calculateAmountDraft(orderItem.platform, settings);

    const updatedOrderItem: AdOrderItemDraft = {
      ...orderItem,
      settings,
      amount: newAmount,
    };

    const updatedOrderItems = [...state.adOrderItems];
    updatedOrderItems[itemIndex] = updatedOrderItem;

    const newTotalPrice = updatedOrderItems.reduce((sum, item) => sum + item.amount, 0);

    return {
      ...state,
      adOrderItems: updatedOrderItems,
      totalPrice: newTotalPrice,
    };
  }),

  // Удаление платформы из черновика заказа
  on(AdOrderDraftActions.removePlatformFromAdOrderDraft, (state, { platformId }) => {
    const itemToRemove = state.adOrderItems.find(item => item.platform.id === platformId);

    if (!itemToRemove) {
      return state;
    }

    const updatedOrderItems = state.adOrderItems.filter(item => item.platform.id !== platformId);

    // Пересчитываем totalPrice на основе обновленных элементов заказа
    const newTotalPrice = updatedOrderItems.reduce((sum, item) => sum + item.amount, 0);

    return {
      ...state,
      adOrderItems: updatedOrderItems,
      totalPrice: newTotalPrice,
    };
  }),

  // Очистка черновика заказа
  on(AdOrderDraftActions.clearAdOrderDraft, () => ({
    ...initialAdOrderDraftState,
  })),

  // Обновление объявления
  on(AdOrderDraftActions.updateAdPublication, (state, { adPublication }) => {
    return {
      ...state,
      adPublication: {
        ...state.adPublication,
        ...adPublication,
      },
    };
  }),

  on(AdOrderDraftActions.upsertSuccess, (state, { serverDraftId, updatedAt }) => ({
    ...state,
    serverDraftId: serverDraftId ?? state.serverDraftId ?? null,
    updatedAt: updatedAt ?? state.updatedAt ?? null,
  })),
);


export function calculateAmountDraft(platform: any, settings: any): number {
  const firstDayPrice = Number(platform.settings.price) || 0;
  const additionalDayPrices = platform.settings.additionalDayPrices || [];

  // Формируем массив цен за каждый день, включая цену за первый день
  const dailyPrices = [firstDayPrice, ...additionalDayPrices];

  // Ограничиваем количество дней размещения доступным числом цен
  const numberOfDays = Math.min(settings.days, dailyPrices.length);

  // Рассчитываем стоимость за дни размещения
  const daysAmount = dailyPrices
    .slice(0, numberOfDays)
    .reduce((sum, price) => sum + Number(price), 0);

  // Рассчитываем стоимость за размещение в топе
  const allowTopPlacement = platform?.settings?.allowTopPlacement !== false;
  const topPricePerHour = allowTopPlacement ? Number(platform.settings.topPricePerHour) || 0 : 0;
  const topAmount = allowTopPlacement ? (settings.topHours || 0) * topPricePerHour : 0;

  // Общая сумма
  const totalAmount = daysAmount + topAmount;

  return totalAmount;
}
